/**
 * Purpose: Bottom bar showing all players with avatars, scores, and active player highlighted.
 * Owns: Player score display, active-player indicator, compact layout for small screens.
 * Notes: This bar is always visible during gameplay. It must be slim enough to not
 *        compete with the question card but readable from across a car.
 */

import type { Player } from '../../types/game';

interface ScoreboardProps {
  /** All players in turn order */
  players: Player[];
  /** ID of the player whose turn it currently is */
  activePlayerId: string;
}

export function Scoreboard({ players, activePlayerId }: ScoreboardProps) {
  return (
    <div style={styles.bar} role="region" aria-label="Scoreboard">
      {players.map((player) => {
        const isActive = player.id === activePlayerId;
        return (
          <div
            key={player.id}
            style={{
              ...styles.playerChip,
              ...(isActive ? styles.activeChip : {}),
            }}
            aria-label={`${player.name}: ${player.score} points${isActive ? ' (current turn)' : ''}`}
          >
            {/* Colored avatar circle with player initial */}
            <div
              style={{
                ...styles.avatar,
                backgroundColor: player.color,
                ...(isActive ? styles.activeAvatar : {}),
              }}
            >
              {player.name.charAt(0).toUpperCase()}
            </div>

            <div style={styles.info}>
              <span style={styles.name}>{player.name}</span>
              <span style={styles.score}>{player.score}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: 'var(--bg-surface)',
    borderRadius: '12px 12px 0 0',
    overflowX: 'auto',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  playerChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.35rem 0.6rem',
    borderRadius: '8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    minWidth: '72px',
  },
  activeChip: {
    backgroundColor: 'rgba(255,255,255,0.12)',
    boxShadow: '0 0 0 2px var(--accent-gold)',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    flexShrink: 0,
  },
  activeAvatar: {
    boxShadow: '0 0 6px var(--accent-gold)',
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: '1.1',
  },
  name: {
    fontSize: '0.7rem',
    color: 'var(--text-light)',
    opacity: 0.85,
    fontWeight: '500',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '60px',
  },
  score: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-family)',
  },
};
